import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Chart from 'chart.js/auto';
import zoomPlugin from 'chartjs-plugin-zoom';
import { LinearProgress, Popper, Box, Grid,  Typography, Table, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle,DialogContent, IconButton , DialogActions, Button, SpeedDial, SpeedDialAction, } from '@mui/material';
// import { Grid, Popper, Box,  Typography, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { getColor, ALL_METRICS, sanitizeSfr } from '../../constants/constantsAnalytic';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExportMenuSFR from './ExportMenuSFR';

Chart.register(zoomPlugin);

const t = (translate, key, fallback) => {
  const value = typeof translate === 'function' ? translate(key) : key;
  return value && value !== key ? value : fallback;
};

const MONTHS_ES = {
  enero: 0,
  febrero: 1,
  marzo: 2,
  abril: 3,
  mayo: 4,
  junio: 5,
  julio: 6,
  agosto: 7,
  septiembre: 8,
  octubre: 9,
  noviembre: 10,
  diciembre: 11,
};

const parseTemporalLabel = (label) => {
  const normalizedLabel = String(label ?? '').trim();

  const dateMatch = normalizedLabel.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (dateMatch) {
    return { kind: 'day' };
  }

  const weekMatch = normalizedLabel.match(/^Semana\s+(\d{1,2})(?:\s+(\d{4}))?$/i);
  if (weekMatch) {
    return { kind: 'week' };
  }

  const monthMatch = normalizedLabel.match(/^([A-Za-zÁÉÍÓÚáéíóúñÑ]+)\s+(\d{4})$/);
  if (monthMatch) {
    const [, monthNameRaw] = monthMatch;
    if (MONTHS_ES[monthNameRaw.toLowerCase()] !== undefined) {
      return { kind: 'month' };
    }
  }

  return { kind: 'text' };
};

const formatTemporalTickLabel = (label, isInverted) => {
  const labelStr = String(label ?? '').trim();

  if (isInverted) {
    if (labelStr.toUpperCase().startsWith('JAULA')) {
      const [first, ...rest] = labelStr.split(' ');
      return [first, rest.join(' ')];
    }
    return labelStr;
  }

  const parsed = parseTemporalLabel(labelStr);
  if (parsed.kind === 'day') {
    const [day, month, year] = labelStr.split('/');
    return [`${day}/${month}`, year];
  }

  if (parsed.kind === 'week') {
    const weekMatch = labelStr.match(/^Semana\s+(\d{1,2})(?:\s+(\d{4}))?$/i);
    if (!weekMatch) return labelStr;
    const [, week, year] = weekMatch;
    return year ? [`Semana ${week}`, year] : `Semana ${week}`;
  }

  if (parsed.kind === 'month') {
    const monthMatch = labelStr.match(/^([A-Za-zÁÉÍÓÚáéíóúñÑ]+)\s+(\d{4})$/);
    if (!monthMatch) return labelStr;
    const [, monthName, year] = monthMatch;
    return [monthName, year];
  }

  return labelStr;
};

// ---------- helpers / plugins (tus existentes) ----------
function drawRoundedRect(ctx, x, y, width, height, radius, fillColor) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
  ctx.fillStyle = fillColor;
  ctx.fill();
}

const horizontalLinePlugin = {
  id: 'horizontalLinePlugin',
  afterDraw: (chart) => {
    const yValue = 100;
    const yScale = chart.scales.y;
    const xScale = chart.scales.x;
    const ctx = chart.ctx;
    const yPixel = yScale.getPixelForValue(yValue);

    ctx.save();
    ctx.setLineDash([6, 6]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = '#e74e0e';
    ctx.beginPath();
    ctx.moveTo(xScale.left, yPixel);
    ctx.lineTo(xScale.right, yPixel);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    const label = '100';
    ctx.save();
    ctx.font = '0.6vw sans-serif';
    const textMetrics = ctx.measureText(label);
    const textWidth = textMetrics.width;
    const textHeight = 13;
    const padding = 4;
    const boxWidth = textWidth + padding * 2;
    const boxHeight = textHeight + padding * 2;
    const xRect = xScale.right - boxWidth;
    const yRect = yPixel - boxHeight + 8;

    drawRoundedRect(ctx, xRect, yRect, boxWidth, boxHeight, 5, '#e74e0e');
    ctx.fillStyle = '#d3d3d3';
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    ctx.fillText(label, xRect + padding, yRect + padding);
    ctx.restore();
  }
};

const BootstrapTooltip = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    arrow
    disablePortal
    PopperProps={{
      modifiers: [
        { name: 'preventOverflow', options: { boundary: 'scrollParent' } },
        { name: 'flip', options: { boundary: 'scrollParent' } }
      ]
    }}
    classes={{ popper: className }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: 'rgb(0, 0, 0)',
    fontSize: '0.7vw',
    marginBottom: '0.4vh'
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'rgb(0, 0, 0)',
    color: '#c3c3c3',
    borderRadius: '0.5vw',
    fontSize: '0.7vw',
    padding: '1vh 1vw',
    maxWidth: '20vw'
  }
}));

// --------- Componente de tabla del tooltip (pivote) ----------
function TooltipPivotTable({ title, axisTitles, rows, titulo, translate }) {
  return (
    <div style={{  maxWidth: '40vw'   }}>
      {title ? (
         <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#a1a1a1', textAlign: 'center', mb: '.5vh', fontSize:'.85vw' }}>
            {title}
          </Typography>
      ) : null}
      <div style={{ maxHeight: '30vh', overflowY: 'auto',}}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ py: '.2vw', fontSize: '0.7vw', color: '#a1a1a1' }}>
                {rows.isFecha || rows.isDate ? t(translate, 'dates', 'Fechas') : titulo}
              </TableCell>
              {axisTitles.map(a => (
                <TableCell key={a.id} align="center" sx={{ py: '1vh', fontSize: '0.7vw', color: a.color, fontWeight: 800, whiteSpace: 'nowrap' }}>
                  {a.title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody >
            {rows.order.map(lbl => {
              const perAxis = rows.matrix.get(lbl) || {};
              const firstAxisId = axisTitles[0]?.id;
              const labelColor = perAxis[firstAxisId]?.color || '#a1a1a1';

              return (
                <TableRow key={lbl} style={{border:'solid 0.01vh'}}>
                  <TableCell sx={{ py: 0.5, fontSize: '0.7vw', color: labelColor, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {lbl}
                  </TableCell>
                  {axisTitles.map(a => {
                    const cell = perAxis[a.id];
                    const raw = cell?.value;
                    let txt;
                    if (raw === null || raw === undefined || raw === '') {
                      txt = '--';
                    } else {
                      txt = (typeof raw === 'number') ? (Number.isInteger(raw) ? raw : Number(raw).toFixed(2)) : raw;
                    }
                    return (
                      <TableCell key={a.id} align="center" sx={{ py: 0.5 }}>
                        <span style={{ color: '#a1a1a1', fontWeight: 700,fontSize: '0.7vw', }}>{txt}</span>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

const SfrChart = ({
  chartData,
  cardStyles,
  Leyendas,
  styleCardCompararSFR,
  nivelSeleccionado,
  InfoIconStyle,
  SwapVertIconStyle,
  colorManual = false,
  color,
  jaulaSeleccionada,
  invertVS=true,
  titulo,
  translate,
  selectedDay,
  viewMode,
}) => {
  const sfrMetric = ALL_METRICS.find(m => m.key === 'sfr_value') || {};
  // const [invertGlobal, setInvertGlobal] = useState(invertVS);
  // const [invert, setInvert] = useState(invertVS);
  // refs al contenedor que envuelve leyenda + canvas
  const modalChartRef = useRef(null);
  const [progressPDF, setProgressPDF] = useState(0);
  const [loadingPDF, setLoadingPDF] = useState(false);

  const [invert, setInvert] = useState(invertVS ? true : false);
  
  // const invert = invertGlobal !== invert;

  const filteredBySelectedDay = useMemo(() => {
    const safeChartData = Array.isArray(chartData) ? chartData : [];

    if (viewMode !== 'day' || !selectedDay) {
      return safeChartData;
    }

    return safeChartData.map((entry) => {
      const summary = Array.isArray(entry?.values_summary?.[0])
        ? entry.values_summary[0]
        : entry?.values_summary;
      const safeSummary = Array.isArray(summary) ? summary : [];

      return {
        ...entry,
        values_summary: safeSummary.filter((item) => item?.day === selectedDay),
      };
    });
  }, [chartData, selectedDay, viewMode]);

  const usedChartData = useMemo(() => {
    const safeChartData = Array.isArray(filteredBySelectedDay) ? filteredBySelectedDay : [];
    return jaulaSeleccionada
      ? safeChartData.filter((j) => j.name === jaulaSeleccionada)
      : safeChartData;
  }, [filteredBySelectedDay, jaulaSeleccionada]);

  const processedChartData = useMemo(() => {
    if (!invert) return usedChartData;
    return [...usedChartData].sort((a, b) => {
      const nameA = String(a.name ?? '');
      const nameB = String(b.name ?? '');
      return nameA.localeCompare(nameB);
    });
  }, [usedChartData, invert]);

  // --- days comunes ----------
  let commonDays;
  if (nivelSeleccionado === 'area' || nivelSeleccionado === 'region') {
    const rawSummary = usedChartData?.[0]?.values_summary;
    const summaryArray = Array.isArray(rawSummary)
      ? rawSummary
      : Object.entries(rawSummary || {}).map(([day, value]) => ({ day, value }));
    commonDays = summaryArray.map(item => item.day);
  } else if (nivelSeleccionado === 'centro') {
    const singleChart = usedChartData?.[0];
    const summaryArray = Array.isArray(singleChart?.values_summary?.[0])
      ? singleChart?.values_summary[0]
      : singleChart?.values_summary;
    commonDays = summaryArray?.map(item => item.day) ?? [];
  } else {
    commonDays = [];
  }
  const safeCommonDays = Array.isArray(commonDays) ? commonDays : [];

  // --- data del bar chart ----------
  const resolveSummary = (entry) => {
    if (!entry?.values_summary) return [];
    return Array.isArray(entry.values_summary[0])
      ? entry.values_summary[0]
      : entry.values_summary;
  };
  // --- visibilidad datasets (debe ir antes del useMemo)
  const [datasetsVisibilitySFR, setDatasetsVisibilitySFR] = useState([]);

  // Ahora sí, puedes crear sfrValueDailyData
  const sfrValueDailyData = useMemo(() => {
    const baseData = invert
      ? {
          labels: processedChartData?.map(j => j.label ?? j.name) ?? [],
          datasets: safeCommonDays.map((day, dayIndex) => {
            const dataArray = (processedChartData ?? []).map(j => {
              const arr = resolveSummary(j);
              // SFR saneado: >1000% se recalcula/descarta (null = hueco); día ausente = 0
              return sanitizeSfr(arr?.[dayIndex]?.value);
            });
            return {
              label: day,
              data: dataArray,
              backgroundColor: getColor(dayIndex),
            };
          }),
        }
      : {
          labels: safeCommonDays,
          datasets: (usedChartData ?? []).map((j, idx) => {
            const arr = resolveSummary(j);
            // SFR saneado: >1000% se recalcula/descarta (null = hueco)
            const data = arr.map(item => sanitizeSfr(item.value));
            return {
              label: j.label ?? j.name,
              entityKey: j.name,
              data,
              backgroundColor: colorManual ? color : getColor(idx),
            };
          }),
        };

    // 👇 sincronizar visibilidad
    baseData.datasets.forEach((ds, idx) => {
      ds.hidden = datasetsVisibilitySFR[idx] === false;
    });

    return baseData;
  }, [invert, usedChartData, processedChartData, safeCommonDays, datasetsVisibilitySFR]);

  const hasDataArray = sfrValueDailyData?.datasets.map(
    ds => Array.isArray(ds.data) && ds.data.some(v => v != null && v !== 0)
  );

  // Si el array de visibilidad está vacío (primera carga), inicialízalo
  useEffect(() => {
    if (datasetsVisibilitySFR.length === 0 && hasDataArray) {
      setDatasetsVisibilitySFR(hasDataArray);
    }
  }, [hasDataArray]);


    // modal de descargar pdf con opciones
  const [dialogOpen, setDialogOpen] = useState(false);
  const handleOpenDialog = () => setDialogOpen(true);

  // === Estado para expansión en modal ===
  const [expanded, setExpanded] = useState(false);

  const toggleDatasetVisibilitySFR = (datasetIndex) => {
    const chart = sfrValueChartInstanceRef.current;
    if (!chart) return;

    const willShow = !chart.isDatasetVisible(datasetIndex);
    chart.setDatasetVisibility(datasetIndex, willShow);
    setDatasetsVisibilitySFR(prev => {
      const updated = [...prev];
      updated[datasetIndex] = willShow;
      return updated;
    });

    // Recalcular límites dinámicos
    const visibleDatasets = chart.data.datasets.filter((_, idx) => chart.isDatasetVisible(idx));
    const values = visibleDatasets.flatMap(ds => ds.data);
    const newMax = Math.round((Math.max(...values, 0) * 1.1) / 10) * 10;
    chart.options.scales.y.max = newMax < 100 ? 110 : newMax;

    chart.update();
  };


  // --- escalas y opciones ----------
  const allValues = sfrValueDailyData.datasets.flatMap(ds => ds.data);
  const rawMax = Math.max(...allValues, 0);
  const calculatedMax = Math.round((rawMax * 1.1) / 10) * 10;
  const yMax = calculatedMax < 100 ? 110 : calculatedMax;

  const borderWidth = 0.2;
  const colorLine = 'rgba(126, 121, 121, 0.38)';
  const fontSizeEjes = window.innerWidth * 0.0065;

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    elements: { rectangle: { minBarLength: 20 } }
  };

  // --- refs chart y canvas ----------
  const sfrValueChartRef = useRef(null);
  const sfrValueChartInstanceRef = useRef(null);
  const expandedCanvasRef = useRef(null);

  const originalXRef = useRef({ min: 0, max: 0 });

  // --- Popper (MUI) para tooltip ----------
  const popperAnchorRef = useRef({
    getBoundingClientRect: () => new DOMRect(),
    contextElement: null
  });
  const hideTimerRef = useRef(null);
  const hideScheduledRef = useRef(false);
  const tooltipLockedRef = useRef(false);

  const [popperOpen, setPopperOpen] = useState(false);
  const [popperPlacement, setPopperPlacement] = useState('top'); // 'top' | 'bottom'

  const [popperData, setPopperData] = useState({
    title: '',
    axisTitles: [],
    rows: { order: [], matrix: new Map(), isDate: false },
    placement: 'top'
  });

  // ---- external handler con MUI Popper ----
  const externalBarTooltipHandler = useCallback((context) => {
    const { chart, tooltip } = context;
    
    // Guards por destrucción/recreación entre frames
    if (!chart || chart._destroyed || !chart.canvas) return;
    
    // no data
    if (!tooltip.dataPoints?.length) {
      if (!tooltipLockedRef.current) setPopperOpen(false);
      return;
    }

    // manejar ocultamiento con “gracia”
    if (tooltip.opacity === 0) {
      if (tooltipLockedRef.current) return;
      if (!hideScheduledRef.current) {
        hideScheduledRef.current = true;
        hideTimerRef.current = setTimeout(() => {
          hideScheduledRef.current = false;
          if (!tooltipLockedRef.current) setPopperOpen(false);
          hideTimerRef.current = null;
        }, 280);
      }
      return;
    }
    // visible → cancela cierres
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
      hideScheduledRef.current = false;
    }

    // posición exacta del caret
    // const rect = chart.canvas.getBoundingClientRect();
    // const pageX = rect.left + window.pageXOffset + tooltip.caretX;
    // const pageY = rect.top  + window.pageYOffset + tooltip.caretY;
    // popperAnchorRef.current.getBoundingClientRect = () => ({
    //   x: pageX, y: pageY, left: pageX, right: pageX, top: pageY, bottom: pageY, width: 0, height: 0, toJSON: () => {}
    // });

    // ===== Centro del canvas + arriba/abajo por espacio disponible =====
    // const rect = chart.canvas.getBoundingClientRect();
    // const centerX = rect.left + rect.width / 2;
    // const spaceAbove = rect.top;
    // const spaceBelow = window.innerHeight - rect.bottom;
    // const preferBottom = spaceBelow >= spaceAbove; // más espacio abajo ⇒ abajo
    // const anchorY = preferBottom ? rect.bottom + 8 : rect.top - 8; // 8px gap
    // const pageX = centerX + window.pageXOffset;
    // const pageY = anchorY + window.pageYOffset;
    // popperAnchorRef.current.getBoundingClientRect = () => ({
    //   x: pageX, y: pageY, left: pageX, right: pageX, top: pageY, bottom: pageY,
    //   width: 0, height: 0, toJSON: () => {}
    // });
    // setPopperPlacement(preferBottom ? 'bottom' : 'top');
    const rect = chart.canvas.getBoundingClientRect();

      // Posición del caret del tooltip (posición exacta del mouse sobre el canvas)
      const caretX = tooltip.caretX;
      const caretY = tooltip.caretY;

      // Calculamos posición base sobre la página
      const pageX = rect.left + caretX + window.pageXOffset;
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      const preferBottom = spaceBelow >= spaceAbove;
      const anchorY = preferBottom ? rect.bottom + 8 : rect.top - 8;
      const pageY = anchorY + window.pageYOffset;

      // Ajuste de límites: evitar que el tooltip se salga del viewport
      const safeX = Math.max(16, Math.min(pageX, window.innerWidth - 16));

      popperAnchorRef.current.getBoundingClientRect = () => ({
        x: safeX,
        y: pageY,
        left: safeX,
        right: safeX,
        top: pageY,
        bottom: pageY,
        width: 0,
        height: 0,
        toJSON: () => {}
      });

      setPopperPlacement(preferBottom ? 'bottom' : 'top');



    // === construir contenido tipo “pivote” tal como tenías ===
    // 1) únicos por dataset
    const uniques = [];
    const seen = new Set();
    (tooltip.dataPoints || []).forEach(dp => {
      if (!seen.has(dp.datasetIndex)) {
        seen.add(dp.datasetIndex);
        uniques.push(dp);
      }
    });
    if (!uniques.length) { setPopperOpen(false); return; }

    // 2) agrupar por eje (y/y1…)
    const groups = uniques.reduce((acc, dp) => {
      const axisID = chart.data.datasets[dp.datasetIndex].yAxisID || 'y';
      (acc[axisID] ||= []).push(dp);
      return acc;
    }, {});
    const axisIDs = Object.keys(groups);

    // 3) título (suele ser label X)
    const title = tooltip.title?.[0] || '';

    // 4) títulos de ejes (limpia [..] si existe) + color ticks
    const axisTitles = axisIDs.map(id => {
      const scaleOpts = chart.options.scales[id] || {};
      const titleArr = scaleOpts.title?.text;
      let axisTitle = Array.isArray(titleArr) ? titleArr.join(' ') : (titleArr || id);
      const match = axisTitle.match(/\[(.*?)\]/);
      if (match) axisTitle = match[1];
      return {
        id,
        title: axisTitle,
        color: (scaleOpts.ticks?.color || 'rgba(255,255,255,.8)')
      };
    });

    // 5) orden base por primer eje
    const baseAxisId = axisIDs[0];
    const labelOrder = groups[baseAxisId].map(dp => (chart.data.datasets[dp.datasetIndex]?.label ?? ''));

    // 6) matriz etiqueta -> eje -> { value, color }
    const matrix = new Map();
    axisIDs.forEach(axisID => {
      groups[axisID].forEach(dp => {
        const ds = chart.data.datasets[dp.datasetIndex];
        const etiqueta = ds?.label ?? '';
        const val = dp.parsed?.y ?? '';
        let dsColor = ds.textColor || ds.borderColor || ds.backgroundColor || '#cfcfcf';
        if (Array.isArray(dsColor)) dsColor = dsColor[dp.dataIndex];
        if (!matrix.has(etiqueta)) matrix.set(etiqueta, {});
        matrix.get(etiqueta)[axisID] = { value: val, color: dsColor };
      });
    });

    // 7) detectar si todas las etiquetas son fechas dd/mm/aaaa
    const fechaRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    const isDate = labelOrder.every(lbl => fechaRegex.test(lbl));

    // 8) setear estado del Popper
    setPopperData({
      title,
      axisTitles,
      rows: { order: labelOrder, matrix, isDate },
      placement: preferBottom ? 'bottom' : 'top'
    });
    setPopperOpen(true);
  }, []);

  // ------- opciones chart con external (usa rAF para suavizar) -------
  const options = useMemo(() => {
    const originalXMin = 0;
    const originalXMax = invert
      ? (processedChartData.length - 1)
      : (safeCommonDays.length - 1);
    originalXRef.current = { min: originalXMin, max: originalXMax };

    return {
      ...barOptions,
      plugins: {
        tooltip: {
          enabled: false,
          mode: 'index',
          intersect: false,
          external: (ctx) => {
            if (typeof window !== 'undefined') {
              if (externalBarTooltipHandler.raf) cancelAnimationFrame(externalBarTooltipHandler.raf);
              externalBarTooltipHandler.raf = requestAnimationFrame(() => externalBarTooltipHandler(ctx));
            } else {
              externalBarTooltipHandler(ctx);
            }
          }
        },
        legend: { display: false },
        zoom: {
          limits: { x: { min: originalXMin, max: originalXMax } },
          pan: { enabled: true, mode: 'x' },
          zoom: {
            wheel: { enabled: true },
            pinch: { enabled: true },
            drag: { enabled: true, modifierKey: 'ctrl' },
            mode: 'x',
            onZoom: ({ chart }) => {
              const { min, max } = chart.scales.x;
              const visibleData = chart.data.datasets.flatMap(ds =>
                ds.data.slice(Math.floor(min), Math.ceil(max))
              );

              // Evitar valores vacíos o inválidos
              if (!visibleData.length) return;

              const newMax = Math.max(...visibleData.filter(v => typeof v === 'number' && !isNaN(v)), 0);
              if (!isFinite(newMax)) return;

              chart.options.scales.y.max = Math.round((newMax * 1.1) / 10) * 10;
              chart.update('none');
            }

          }
        }

      },
      scales: {
        x: {
          ticks: {
            font: { size: fontSizeEjes },
            color: '#C3C3C3',
            callback: function (value) {
              const rawLabel = this.getLabelForValue(value);
              return formatTemporalTickLabel(rawLabel, invert);
            }
          },
          grid: { color: colorLine, lineWidth: borderWidth },
          min: originalXMin,
          max: originalXMax
        },
        y: {
          max: yMax,
          title: { display: false, text: 'SFR' },
          ticks: { font: { size: fontSizeEjes }, color: sfrMetric.color || '#C3C3C3' },
          grid: { color: colorLine, lineWidth: borderWidth }
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invert, processedChartData.length, safeCommonDays.length, yMax, invert]);

  // ------ montar chart una vez y actualizar cuando cambien data/opciones ------
  useEffect(() => {
    if (!sfrValueChartRef.current) return;

    const chart = new Chart(sfrValueChartRef.current, {
      type: 'bar',
      data: sfrValueDailyData,
      plugins: [horizontalLinePlugin],
      options
    });
    sfrValueChartInstanceRef.current = chart;

    // mousemove para coord del mouse
    const canvas = sfrValueChartRef.current;
    const handleMouseMove = (e) => {
      // no hace falta guardarlo: usamos caretX/caretY
      // pero mantenerlo no molesta si luego quieres "nearest"
      chart._tooltipMouseX = e.clientX;
      chart._tooltipMouseY = e.clientY;
    };
    canvas.addEventListener('mousemove', handleMouseMove);

    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      
      if (externalBarTooltipHandler.raf) {
        cancelAnimationFrame(externalBarTooltipHandler.raf);
        externalBarTooltipHandler.raf = null;
      }

      chart.destroy();
      sfrValueChartInstanceRef.current = null;
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideScheduledRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // actualizar data del chart sin destruir
  useEffect(() => {
    const chart = sfrValueChartInstanceRef.current;
    if (!chart) return;
    chart.data = sfrValueDailyData;
    chart.update('none');
  }, [sfrValueDailyData]);

  // recalcular y.max al cambiar visibilidad/datos
  useEffect(() => {
    const chart = sfrValueChartInstanceRef.current;
    if (!chart) return;
    // const visibleDatasets = chart.data.datasets.filter((_, idx) => chart.isDatasetVisible(idx));
    const visibleDatasets = chart.data.datasets.filter((_, idx) => chart.isDatasetVisible(idx));
    if (!visibleDatasets.length) return;

    const vals = visibleDatasets.flatMap(ds => ds.data);
    const rMax = Math.max(...vals, 0);
    const calcMax = Math.round((rMax * 1.1) / 10) * 10;
    chart.options.scales.y.max = calcMax < 100 ? 110 : calcMax;
    chart.update('none');
  }, [datasetsVisibilitySFR, invert, chartData]);


    // Tamaños para el canvas según modo
  const expandedChartBoxStyle = { height: '43vh', width: '79vw', padding: '1vh 1vw' };

    // ================== CHART MODAL ==================
    const createModalChart = () => {
      if (!expandedCanvasRef.current) return;
      modalChartRef.current = new Chart(expandedCanvasRef.current, {
        type: 'bar',
        data: sfrValueDailyData,
        plugins: [horizontalLinePlugin],
        options,
      });
    };
  
    const destroyModalChart = () => {
      if (modalChartRef.current) {
        modalChartRef.current.destroy();
        modalChartRef.current = null;
      }
    };

    useEffect(() => {
      if (modalChartRef.current) {
        modalChartRef.current.data = sfrValueDailyData;
        modalChartRef.current.update('none');
      }
    }, [invert]);
  

  return (
    <Grid item xs={nivelSeleccionado === 'centro' ? 6 : 12} style={{ position: 'relative', width: '100%', height: '100%' }}>

      {loadingPDF && (
        <Box sx={{ width: '50%', mt: '1.3vh',marginLeft:'17vw', position:'absolute' }}>
          <LinearProgress
            variant="determinate"
            value={progressPDF}
            // sx={{ height: 8, borderRadius: 2 }}
            sx={{
                height: '.8vh',
                borderRadius: '.5vw',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#e94e1a',   // 👈 color de la barra
                },
                backgroundColor: 'rgba(233,78,26,0.2)' // 👈 opcional, color del fondo
              }}
          />
          <p  sx={{ mt: '.25vh', textAlign: "center" }}>
            {progressPDF}% {t(translate, 'generating_pdf', 'Generando PDF...')}
          </p>
        </Box>
      )}
      

      <div className='card' style={{...cardStyles.large, position: "relative"}}>
      {/* <div style={{ marginTop:'4vh', width: '100%', height: '100%'}}/> */}
            {/* === Opciones de descarga y ampliar grafico === */}
        <div style={{ position: "relative",  width:'100%' }}>
          <SpeedDial
            ariaLabel="Opciones"
            icon={<MoreVertIcon sx={{color: '#c3c3c3 !important', fontSize:'1vw !important'}}  />}
            direction="down" // puedes usar "left", "right", "up"
            FabProps={{
              sx: {
                width: "2vw",
                height: "2vw",
                background: "rgba(0, 0, 0, 0)",
                boxShadow:0,
                "&:hover": { background: "rgba(0,0,0,0)" },
                marginBottom: "-2vh", 
              },
            }}
            sx={{
              position: "absolute",
              right: "0vw",
              top: ".3vh",
              zIndex: 10,
              color: '#c3c3c3 !important', fontSize:'1vw !important'
            }}
          >
            <SpeedDialAction
              key="expand"
              icon={<OpenInFullIcon className='icon-m' sx={{ fontSize: ".8vw !important" }} />}
              tooltipTitle={t(translate, 'expand', 'Expandir')}
              onClick={() => setExpanded(true)}
              FabProps={{
                sx:{
                  background: "rgba(0,0,0,0.95)",
                  color: "white",
                  width: "1.5vw",
                  height: "1.5vw",
                  boxShadow: "none",
                  "&:hover": { background: "rgba(0,0,0,0.8)" },
                  marginBottom: "-.2vh", 
      
                }
              }}
            />
            <SpeedDialAction
              key="download"
              icon={<FileDownloadIcon className='icon-m' sx={{ fontSize: ".8vw !important" }} />}
              tooltipTitle={loadingPDF ? `${t(translate, 'generating_pdf', 'Generando PDF...')} (${progressPDF}%)` : t(translate, 'download', 'Descargar')}
              onClick={handleOpenDialog}
              FabProps={{
                disabled: loadingPDF,
                sx:{
                  background: "rgba(0,0,0,0.95)",
                  color: "white",
                  width: "1.5vw",
                  height: "1.5vw",
                  boxShadow: "none",
                  "&:hover": { background: "rgba(0,0,0,0.8)" },
      
                }
              }}
            />
            
          </SpeedDial>
          <ExportMenuSFR
            chartInstanceRef={sfrValueChartInstanceRef}
            invert={invert}
            horizontalLinePlugin={horizontalLinePlugin}
            dialogOpen={dialogOpen}
            setDialogOpen={setDialogOpen}
            setLoadingPDF={setLoadingPDF}
            setProgressPDF={setProgressPDF}
            translate={translate}
          />
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignContent: 'center',
            alignItems: 'center',
            // width: '40%',
            width: '80%',
            // border:'solid',
            top: '1vh',
            left: nivelSeleccionado ==='centro' ? '0vw' : '-10vw',
            // marginBottom: '-1.2vh',
            // position:'absolute'
          }}
        >
          <SwapVertIcon
            onClick={() => setInvert(!invert)}
            style={{ color: '#c3c3c3', marginLeft: SwapVertIconStyle, fontSize: '1.3vw' }}
          />
          <h2 className='h2' style={{  color: sfrMetric.color, marginLeft: '1vw', }}>{t(translate, 'corrected_sfr', 'SFR Corregido [ % ]')}</h2>
          <BootstrapTooltip title={t(translate, 'delivered_food_goal_ratio', 'Alimento entregado / meta del dia')}>
            <InfoIcon
              onClick={() => setInvert(!invert)}
              style={{ color: '#c3c3c3', marginLeft: InfoIconStyle, fontSize: '1vw' }}
            />
          </BootstrapTooltip>
        </div>
        <Leyendas
          data={sfrValueDailyData}
          toggle={toggleDatasetVisibilitySFR}
          visible={datasetsVisibilitySFR}
          sfrChart={nivelSeleccionado === 'area'}
          translate={translate}
        />

        <div style={{...styleCardCompararSFR, }}>
          <canvas ref={sfrValueChartRef} tabIndex="0" />
        </div>
      </div>

      {/* --- Popper MUI anclado al punto del mouse --- */}
      <Popper
        open={popperOpen}
        anchorEl={popperAnchorRef.current}
        placement={popperPlacement}
        modifiers={[
          { name: 'offset', options: { offset: [0, 6] } }, // sin gap para entrar más fácil
          { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
          { name: 'flip', enabled: true },
        ]}
        sx={{ zIndex: 1300, pointerEvents: 'auto' }}
      >
        <Box
          elevation={3}
          sx={{
            p: '0.5vw',
            maxHeight: '40vh',
            overflowY: 'auto',
            bgcolor: 'rgba(0,0,0,0.96)',
            color: '#c3c3c3',
            border: '0.01vw solid #e84e0e',
            borderRadius: '8px',
            minWidth: 260,
          }}
          onMouseEnter={() => {
            tooltipLockedRef.current = true;
            if (hideTimerRef.current) {
              clearTimeout(hideTimerRef.current);
              hideTimerRef.current = null;
              hideScheduledRef.current = false;
            }
          }}
          onMouseLeave={() => {
            tooltipLockedRef.current = false;
            setPopperOpen(false);
          }}
          onWheel={(e) => e.stopPropagation()}
        >
          <TooltipPivotTable
            title={popperData.title}
            axisTitles={popperData.axisTitles}
            rows={popperData.rows}
            titulo={titulo}
            translate={translate}
          />
        </Box>
      </Popper>

      {/* === MODAL EXPANDIDO === */}
      <Dialog
        open={expanded}
        onClose={() => setExpanded(false)}
        PaperProps={{
          sx: {
            bgcolor: '#0c0c0c !important', 
            color: '#e0e0e0',
            minWidth: '81.5vw !important',
            height: '64.5vh',
            top: '16.2vh',
            right: '8vw',
            borderRadius: '0.4vw',
            boxShadow: 'none',
          },
        }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0)' } } }}
        TransitionProps={{
          onEntered: () => createModalChart(),
          onExited: () => destroyModalChart(),
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start', bgcolor: '#0c0c0c', }}>
          <Box>
            <IconButton onClick={() => setInvert(!invert)} aria-label={t(translate, 'invert_axes', 'Invertir ejes')} sx={{ mr: 1 }}>
              <SwapVertIcon className="icon-m" style={{ fontSize: '1.3vw', width: '1.2vw', aspectRatio: 1 }} />
            </IconButton>
          </Box>
          <Box sx={{ mb: 2, width: '55vw'}}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignContent: 'flex-start',
                alignItems: 'flex-start',
                width: '87%',
                scale: '1.2',
                marginTop: '1.5vh',
                marginLeft: '5vw',
              }}
            >
              <h2 style={{ color: sfrMetric.color }}>{t(translate, 'corrected_sfr', 'SFR Corregido [ % ]')}</h2>
            </div>
          </Box>
          <DialogActions>
            <Button onClick={() => setExpanded(false)} sx={{ marginLeft:'20vw'}} startIcon={<CloseFullscreenIcon style={{fontSize:'1vw', color:'#c3c3c3',}} />}></Button>
          </DialogActions>
        </DialogTitle>
        
        <DialogContent dividers sx={{ bgcolor: '#0c0c0c', color: '#e0e0e0',}}>
          <Box sx={{ mb: 1 }}>
            <Leyendas
              data={sfrValueDailyData}
              toggle={toggleDatasetVisibilitySFR}
              visible={datasetsVisibilitySFR}
              width={'75vw'}
              translate={translate}
            />
          </Box>
          <Box sx={expandedChartBoxStyle}>
            {expanded && <canvas ref={expandedCanvasRef} />}
          </Box>
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

export default SfrChart;
